import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormField } from '@ipfabric/ui-components/FormField/FormField';
import {
  VALID_PASSWORD_REGEX,
  VALID_USERNAME_REGEX,
  isNullable,
} from '@ipfabric/utils';
import { ReduxFormTextField } from 'components/ReduxFormTextField/ReduxFormTextField';
import { ReduxFormToggleField } from 'components/ReduxFormToggleField/ReduxFormToggleField';
import SelectField from 'components/SelectField';
import { useLocale } from 'containers/App/LocaleContext';
import ScheduleField from 'containers/ScheduleField';
import type { Map as ImmutableMap } from 'immutable';
import { type FC, memo } from 'react';
import type { FormErrors, InjectedFormProps } from 'redux-form';
import { Field, Form, reduxForm } from 'redux-form/immutable';
import { handleSubmitFail } from 'utils/formValidation';
import { isFtp } from '../helpers';
import type { CronFormValue } from '../types';
import { BACKUP_FORM_NAME, FTP_TYPE_OPTIONS } from './constants';

const { formValues } = require('redux-form/immutable');

export type BackupFormValuesType = string | boolean | CronFormValue;

export interface BackupFormValues
  extends ImmutableMap<string, BackupFormValuesType> {
  enabled: string | boolean;
  cron: CronFormValue;
  type: 'local' | 'ftp' | 'sftp';
  server: string | null;
  username: string | null;
  password: string | null;
  directory: string | null;
  hasStoredFTPPassword: boolean | null;
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy code
const validate = (values: BackupFormValues): FormErrors<BackupFormValues> => {
  const errors: {
    [Key in keyof BackupFormValues & string]?: string;
  } = {};

  try {
    const enabled = values.get('enabled');
    if (enabled) {
      const checkField = (field: keyof BackupFormValues & string) => {
        const value = values.get(field);
        if (!value) {
          errors[field] = 'required';
        }
        if (typeof value === 'string' && value.trim().length === 0) {
          errors[field] = 'required';
        }
        return value;
      };
      const cron = checkField('cron');
      const type = checkField('type');
      if (isFtp(type)) {
        const password = values.get('password');
        if (
          typeof password === 'string' &&
          !VALID_PASSWORD_REGEX.test(password)
        ) {
          errors.password = `Password must match pattern: ${VALID_PASSWORD_REGEX}`;
        }

        const username = values.get('username');
        if (
          typeof username !== 'string' ||
          !VALID_USERNAME_REGEX.test(username)
        ) {
          errors.username = `Username must match pattern: ${VALID_USERNAME_REGEX}`;
        }

        checkField('server');
        checkField('username');
        if (!values.get('hasStoredFTPPassword')) {
          checkField('password');
        }
        checkField('directory');
      }

      const directory = values.get('directory');
      if (
        type === 'ftp' &&
        typeof directory === 'string' &&
        directory.startsWith('/')
      ) {
        errors.directory = 'absolutePath';
      }

      if (typeof cron === 'object') {
        const cronExpression = cron.expression;
        if (typeof cronExpression === 'string') {
          // TODO: Must be rewritten, doubled logic here and ScheduleField components also
          const split = cronExpression.split(' ');
          const first = split[0];

          if (first === '*' || first === '' || isNullable(first)) {
            errors.cron = 'required';
          }
        }
      }

      if (type && !FTP_TYPE_OPTIONS.find((i) => i.value === type)) {
        errors.type = 'incorrectValue';
      }
    }
    return errors;
  } catch {
    return { _error: '' };
  }
};

const BareBackup: FC<
  InjectedFormProps<BackupFormValues> & BackupFormValues
> = ({ enabled, type, handleSubmit, submitting, hasStoredFTPPassword }) => {
  const locale = useLocale();
  const msg = locale.systemBackupMaintenance.backup;
  const msgScheduleField = locale.scheduleField;

  return (
    <Card>
      <Form
        className='p flex flex-col gap'
        onSubmit={handleSubmit}
        autoComplete='off'
      >
        <span className='text-level-5 font-semibold'>
          Schedule system backup
        </span>
        <fieldset className='p-0' disabled={submitting}>
          <Field
            inline
            label='Enable backup job'
            validationMessages={msg.validation}
            component={ReduxFormToggleField}
            name='enabled'
          />
        </fieldset>
        {/* Negative margin used here to offset old schedule field component in new layout without undue changes */}
        <fieldset className='p-0 -mb-3' disabled={!enabled || submitting}>
          <Field
            inline
            className='form-control'
            component={ScheduleField}
            disabled={!enabled}
            id='cron'
            msg={msgScheduleField}
            name='cron'
            type='text'
          />
        </fieldset>
        <fieldset
          className='p-0 flex flex-col gap max-w-96'
          disabled={!enabled || submitting}
        >
          <FormField label='Destination'>
            <Field
              block
              component={SelectField}
              id='type'
              msg={msg}
              name='type'
              type='text'
              clearable={false}
              options={FTP_TYPE_OPTIONS}
              disabled={!enabled || submitting}
            />
          </FormField>
          {isFtp(type) && (
            <section>
              <Field
                label='Server'
                component={ReduxFormTextField}
                validationMessages={msg.validation}
                name='server'
              />
              <Field
                label='Username'
                component={ReduxFormTextField}
                validationMessages={msg.validation}
                name='username'
              />
              <Field
                type='password'
                label='Password'
                component={ReduxFormTextField}
                validationMessages={msg.validation}
                name='password'
                placeholder={
                  hasStoredFTPPassword ? 'Fill to change stored password' : ''
                }
              />
              <Field
                label='Directory'
                component={ReduxFormTextField}
                validationMessages={msg.validation}
                name='directory'
              />
            </section>
          )}
        </fieldset>
        <fieldset className='p-0 flex gap-xs max-w-40'>
          <Button
            className='flex-grow'
            variant='primary'
            type='submit'
            loading={submitting}
            icon='fa-save'
          >
            Save
          </Button>
        </fieldset>
      </Form>
    </Card>
  );
};

export const Backup = memo(
  reduxForm<BackupFormValues>({
    form: BACKUP_FORM_NAME,
    enableReinitialize: true,
    onSubmitFail: handleSubmitFail(),
    validate,
  })(formValues('type', 'enabled', 'hasStoredFTPPassword')(BareBackup)),
);
